import {
  ImageAddAction,
  ImageSetContentAction,
  KalturaAsset,
  KalturaAssetArgs,
  KalturaBooleanValue,
  KalturaCategoryItem,
  KalturaDoubleValue,
  KalturaImage,
  KalturaImageObjectType,
  KalturaLongValue,
  KalturaMetaDataType,
  KalturaMultilingualStringValue,
  KalturaMultilingualStringValueArray,
  KalturaStringValue,
  KalturaTranslationToken,
  KalturaUnifiedChannel,
  KalturaUrlResource,
} from '@kaltura-ott/ott-client-types/api/types';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, delay, map, mergeMap, retryWhen, tap } from 'rxjs/operators';

import { randString } from '../../fortests/utils';
import { KuxKalturaCategoryItemArgs } from '../../interfaces';
import { kalturaClient } from '../kaltura-clent';

import { addChildCategoryToParent } from './category';
import { getCategoryStructsList } from './category-template';
import { CreateMLCategory, createMultiCategoryV2 } from './category-v2';
import { BASE_KUX_METAS, CATEGORY_ID, CONTENT_KUX_METAS, STYLING_KUX_METAS, WIDGET_KUX_METAS } from './config/metas';

const addImagesToCategory = (categoryId: number, images: Array<KalturaImage>): Observable<void> => {
  const client = kalturaClient();
  const actions: Observable<KalturaImage>[] = [];
  images.forEach((image) => {
    const imageAddAction = new ImageAddAction({
      image: new KalturaImage({
        imageTypeId: image.imageTypeId,
        imageObjectId: categoryId,
        imageObjectType: KalturaImageObjectType.category,
      }),
    });
    actions.push(client.request(imageAddAction));
  });
  return forkJoin(actions).pipe(
    mergeMap((responses: Array<KalturaImage>) => {
      const setContentActions: Observable<void>[] = [];
      responses.forEach((image, index) => {
        const setContentAction = new ImageSetContentAction({
          id: image.id,
          content: new KalturaUrlResource({ url: images[index].url }),
        });
        setContentActions.push(client.request(setContentAction));
      });
      return forkJoin(setContentActions);
    }),
    catchError((err) => {
      return of(err);
    }),
  );
};

/**
 *
 * @param categoriesData
 */
const createMultiCategoryAndAsset = (
  categoriesData: CreateMLCategory[],
): Observable<{ asset: KalturaAsset; category: KalturaCategoryItem }[]> => {
  return createMultiCategoryV2(categoriesData).pipe(
    mergeMap((multiResponse) => {
      const imageToCategoryRequests: Observable<void>[] = [];
      multiResponse.forEach(({ category }, index) => {
        if (categoriesData[index].images) {
          imageToCategoryRequests.push(addImagesToCategory(category.id, categoriesData[index].images));
        }
      });
      if (!imageToCategoryRequests.length) return of(multiResponse);

      return forkJoin(...imageToCategoryRequests).pipe(map(() => multiResponse));
    }),
  );
};

const ALLBASICMETAS = {
  ...CATEGORY_ID,
  ...BASE_KUX_METAS,
  ...STYLING_KUX_METAS,
  ...CONTENT_KUX_METAS,
  ...WIDGET_KUX_METAS,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const assetJsonIntoObject = (jsonAsset: any, language: any): KalturaAssetArgs => {
  const asset: KalturaAssetArgs = {
    multilingualName: [new KalturaTranslationToken({ value: jsonAsset.name, language: language })],
    externalId: randString(16),
    metas: {},
    tags: {},
  };

  Object.keys(jsonAsset.metas).forEach((systemName) => {
    const meta = ALLBASICMETAS[systemName];
    const value = jsonAsset.metas[systemName].value;
    if (meta) {
      switch (meta.dataType) {
        case KalturaMetaDataType.boolean:
          // @ts-expect-error TODO Fix
          asset.metas[systemName] = new KalturaBooleanValue({ value });
          break;
        case KalturaMetaDataType.date:
          // @ts-expect-error TODO Fix
          asset.metas[systemName] = new KalturaLongValue({ value });
          break;
        case KalturaMetaDataType.string:
          // @ts-expect-error TODO Fix
          asset.metas[systemName] = new KalturaStringValue({ value });
          break;
        case KalturaMetaDataType.number:
          // @ts-expect-error TODO Fix
          asset.metas[systemName] = new KalturaDoubleValue({ value });
          break;
        case KalturaMetaDataType.multilingualString:
          // @ts-expect-error TODO Fix
          asset.metas[systemName] = new KalturaDoubleValue({ value });
          break;
      }
    }
  });
  Object.keys(jsonAsset.tags).forEach((systemName) => {
    // @ts-expect-error TODO Fix
    asset.tags[systemName] = new KalturaMultilingualStringValueArray();
    // @ts-expect-error TODO Fix
    asset.tags[systemName].objects = jsonAsset.tags[systemName].objects.map((ob) => {
      return new KalturaMultilingualStringValue({
        multilingualValue: [
          new KalturaTranslationToken({
            value: ob.value,
            language: language,
          }),
        ],
      });
    });
  });
  return asset;
};

export interface ParentingMap {
  [key: string | number]: number;
}

const parentingMap: ParentingMap = {};

export interface StructsMap {
  [key: string]: number;
}

const doMultiImport = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  JSONtreeChildren: any[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  language: any,
  withCollections = false,
  withImages = false,
  withDynamicData = false,
) => {
  return getCategoryStructsList().pipe(
    map((resp) => {
      const structsMap: StructsMap = {};
      resp.objects.forEach((struct) => {
        structsMap[struct.systemName] = struct.id;
      });
      return structsMap;
    }),
    catchError((err) => {
      return of(err);
    }),
    mergeMap((structsMap) => {
      const multiImportData = JSONtreeChildren.map((JSONtree) => {
        const category: KuxKalturaCategoryItemArgs = {
          type: JSONtree.typeSystemName,
          isActive: JSONtree.isActive,
          multilingualName: [new KalturaTranslationToken({ value: JSONtree.name, language: language })],
          referenceId: JSONtree.referenceId,
        };
        if (withCollections && JSONtree.unifiedChannels?.length > 0) {
          category.unifiedChannels = JSONtree.unifiedChannels.map(
            // @ts-expect-error TODO Fix
            (ch) =>
              new KalturaUnifiedChannel({
                id: ch.id,
                type: ch.type,
              }),
          );
        }

        if (withDynamicData && JSONtree.dynamicData) {
          Object.keys(JSONtree.dynamicData).forEach((key) => {
            if (!category.dynamicData) {
              category.dynamicData = {};
            }
            category.dynamicData[key] = new KalturaStringValue({ value: JSONtree.dynamicData[key].value });
          });
        }

        const assetMetadata = assetJsonIntoObject(JSONtree.asset, language);
        assetMetadata.type = structsMap[JSONtree.type];
        const images = withImages && JSONtree.images?.length > 0 ? JSONtree.images : null;
        return { category, assetMetadata, images };
      });

      return createMultiCategoryAndAsset(multiImportData).pipe(
        tap((multiResponse) => {
          multiResponse.forEach((resp, index) => {
            parentingMap[JSONtreeChildren[index].id] = resp.category.id;
          });
        }),
      );
    }),
    mergeMap((multiResponse) => {
      const childrenIDs = multiResponse.map((res) => res.category.id);
      if (JSONtreeChildren[0].oldParentId && parentingMap[JSONtreeChildren[0].oldParentId]) {
        const parentId = parentingMap[JSONtreeChildren[0].oldParentId];
        return addChildCategoryToParent({ childrenIds: childrenIDs, parentId });
      }
      return of(multiResponse);
    }),
    retryWhen((errors) => {
      return errors.pipe(
        delay(2000),
        tap(() => console.log('retrying...', errors)),
      );
    }),
    catchError((err) => {
      return of(err);
    }),
  );
};

export const importTreeV2 = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  JSONtree: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  forkRequests: Observable<any>[] = [],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  language: any,
  withCollections = false,
  withImages = false,
  withDynamicData = false,
) => {
  console.log('MAYBE ==> ', JSONtree.CREATED_ID);
  if (!forkRequests.length) {
    forkRequests.push(doMultiImport([JSONtree], language, withCollections, withImages, withDynamicData));
  }

  if (JSONtree.children.length == 0) {
    return forkRequests;
  }

  if (JSONtree.children.length) {
    // Import
    forkRequests.push(doMultiImport(JSONtree.children, language, withCollections, withImages, withDynamicData));

    // @ts-expect-error TODO Fix
    JSONtree.children.forEach((subJsonTree) => {
      importTreeV2(subJsonTree, forkRequests, language, withCollections, withImages, withDynamicData);
    });
  }
  console.log(forkRequests);
  return forkRequests;
};
